import { sortOrganisations } from "@/utils/organisationSort";

import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Organization } from "@/types/user";
import { User, Briefcase, UserCog, Pencil, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

interface FormValues {
  first_name: string;
  last_name: string;
  organization_id: string;
  role: "standard" | "editor" | "admin" | "superuser" | "partner" | "archived" | "internal_staff" | "advisor" | "production";
}

interface UserRoleEditProps {
  userId: string;
  defaultValues: FormValues;
  onSubmit: (values: FormValues) => Promise<void>;
  isLoading: boolean;
  /** When true, hide superuser-only roles and the org rename/delete controls. */
  restrictToOrgAdmin?: boolean;
}

export function UserRoleEdit({ userId, defaultValues, onSubmit, isLoading, restrictToOrgAdmin = false }: UserRoleEditProps) {
  const form = useForm<FormValues>({ defaultValues });
  const [isRenaming, setIsRenaming] = useState(false);
  const [deleteOrgId, setDeleteOrgId] = useState<string | null>(null);
  const [newOrgName, setNewOrgName] = useState("");
  const [selectedOrgId, setSelectedOrgId] = useState<string>(defaultValues.organization_id || "");

  const { data: organizations, refetch } = useQuery({
    queryKey: ["organizations"],
    queryFn: async () => {
      const { data, error } = await supabase.from('organizations').select('*');
      if (error) throw error;
      return data as Organization[];
    }
  });

  const handleRenameOrg = async () => {
    try {
      const { error } = await supabase
        .from('organizations')
        .update({ name: newOrgName })
        .eq('id', selectedOrgId);

      if (error) throw error;
      
      toast.success('Organization renamed successfully');
      setIsRenaming(false);
      setNewOrgName("");
      refetch();
    } catch (error) {
      toast.error('Failed to rename organization');
      console.error(error);
    }
  };

  const handleDeleteOrg = async (orgId: string) => {
    try {
      const { error } = await supabase
        .from('organizations')
        .delete()
        .eq('id', orgId);

      if (error) throw error;
      
      toast.success('Organization deleted successfully');
      setDeleteOrgId(null);
      refetch();
    } catch (error) {
      toast.error('Failed to delete organization');
      console.error(error);
    }
  };

  const selectedOrg = organizations?.find(org => org.id === selectedOrgId);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="first_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2">
                <User className="h-4 w-4" />
                First Name
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="last_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Last Name
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="organization_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2">
                <Briefcase className="h-4 w-4" />
                Organization
              </FormLabel>
              <div className="flex gap-2">
                <Select 
                  onValueChange={(value) => {
                    field.onChange(value);
                    setSelectedOrgId(value);
                  }} 
                  defaultValue={field.value}
                  disabled={restrictToOrgAdmin}
                >
                  <FormControl>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="Select organization" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {sortOrganisations(organizations ?? [], (org) => org.name).map((org) => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!restrictToOrgAdmin && (
                <>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    setNewOrgName(selectedOrg?.name || '');
                    setIsRenaming(true);
                  }}
                  disabled={!selectedOrg}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  onClick={() => setDeleteOrgId(selectedOrgId)}
                  disabled={!selectedOrg}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
                </>
                )}
              </div>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="role"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2">
                <UserCog className="h-4 w-4" />
                Role
              </FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="editor">Editor (Standard + device edit rights)</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  {!restrictToOrgAdmin && (
                    <>
                      <SelectItem value="partner">Partner</SelectItem>
                      <SelectItem value="superuser">Superuser</SelectItem>
                      <SelectItem value="internal_staff">Staff</SelectItem>
                      <SelectItem value="advisor">Advisor / Board Member</SelectItem>
                      <SelectItem value="production">Production</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </FormItem>
          )}
        />

        <Button type="submit" disabled={isLoading} className="text-black hover:opacity-80" style={{ backgroundColor: "#DFEF97" }}>
          {isLoading ? "Saving..." : "Save Changes"}
        </Button>
      </form>

      <Dialog open={isRenaming} onOpenChange={setIsRenaming}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Organization</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={newOrgName}
              onChange={(e) => setNewOrgName(e.target.value)}
              placeholder="Enter new organization name"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRenaming(false)}>
              Cancel
            </Button>
            <Button onClick={handleRenameOrg} disabled={!newOrgName}>
              Rename
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteOrgId} onOpenChange={(open) => !open && setDeleteOrgId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Organization?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this organization. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteOrgId && handleDeleteOrg(deleteOrgId)}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Form>
  );
}
